import { openDB } from 'idb';

const DB_NAME = 'purelytics';
const DB_VERSION = 6; // Incremented to add emailActivityCache store
const APP_VERSION_KEY = 'purelytics_app_version';
const CURRENT_APP_VERSION = '1.0.1'; // Update this when making schema changes

class IndexedDBService {
  constructor() {
    this.isInitializing = false;
    this.initializationError = null;
    this.initPromise = null;
    this.dbPromise = null;
    this.transactionTimeout = 30000; // 30 seconds timeout for transactions
    this.recoveryAttempts = 0;
    this.MAX_RECOVERY_ATTEMPTS = 3;
    
    // Initialize the database
    this.initDB().catch(error => {
      console.error('Failed to initialize database:', error);
      this.initializationError = error;
    });
  }

  async initDB() {
    // Prevent multiple simultaneous initialization attempts
    if (this.isInitializing) {
      return this.initPromise;
    }

    this.isInitializing = true;
    this.initializationError = null;

    try {
      // Clear any stale sync flags that might be preventing initialization
      const lastSyncTimestamp = localStorage.getItem('lastSyncAttempt');
      const now = Date.now();
      if (lastSyncTimestamp && (now - parseInt(lastSyncTimestamp, 10)) > 5 * 60 * 1000) {
        // If last sync was more than 5 minutes ago, clear the flag
        localStorage.removeItem('syncInProgress');
      }
      
      // Set a timestamp for this sync attempt
      localStorage.setItem('lastSyncAttempt', now.toString());
      
      // Check if app version has changed since last use
      const storedAppVersion = localStorage.getItem(APP_VERSION_KEY);
      
      // If app version changed, we should delete the database and recreate it
      if (storedAppVersion && storedAppVersion !== CURRENT_APP_VERSION) {
        console.log('App version changed. Recreating database...');
        try {
          // Close any existing connections
          if (this.db) {
            this.db.close();
            this.db = null;
          }
          
          // Delete the database
          await deleteDatabase();
        } catch (deleteError) {
          console.warn('Failed to delete database:', deleteError);
          // Continue anyway
        }
      }
      
      // Store current app version
      localStorage.setItem(APP_VERSION_KEY, CURRENT_APP_VERSION);
      
      this.initPromise = this.openDatabase();
      const db = await this.initPromise;
      
      console.log('Database initialized successfully', {
        name: db.name,
        version: db.version,
        stores: Array.from(db.objectStoreNames)
      });
      
      this.db = db;
      this.isInitializing = false;
      
      // Reset recovery attempts on successful initialization
      this.recoveryAttempts = 0;
      
      return db;
    } catch (error) {
      this.isInitializing = false;
      this.initializationError = error;
      console.error('Error initializing database:', error);
      
      // Try recovery actions
      if (error.name === 'VersionError' || error.name === 'AbortError' || error.name === 'QuotaExceededError' || this.recoveryAttempts < this.MAX_RECOVERY_ATTEMPTS) {
        console.log('Attempting database recovery...');
        this.recoveryAttempts++;
        return this.recoverDatabase();
      }
      
      throw error;
    }
  }
  
  async openDatabase() {
    return openDB(DB_NAME, DB_VERSION, {
      upgrade(db, oldVersion, newVersion) {
        // console.log(`Upgrading IndexedDB from version ${oldVersion} to ${newVersion}`);

        // Create stores if they don't exist
        if (!db.objectStoreNames.contains('clients')) {
          // console.log('Creating clients store');
          const clientStore = db.createObjectStore('clients', { keyPath: 'accountId' });
          clientStore.createIndex('clientName', 'clientName');
          clientStore.createIndex('lastSyncedAt', 'lastSyncedAt');
        }

        if (!db.objectStoreNames.contains('locations')) {
          // console.log('Creating locations store');
          const locationStore = db.createObjectStore('locations', { keyPath: 'accountId' });
          locationStore.createIndex('name', 'name');
          locationStore.createIndex('lastSyncedAt', 'lastSyncedAt');
          locationStore.createIndex('featureImageUrl', 'Website_Feature_Image_URL__c');
          locationStore.createIndex('latitude', 'latitude');
          locationStore.createIndex('longitude', 'longitude');
        }

        if (!db.objectStoreNames.contains('purePromoteContacts')) {
          // console.log('Creating purePromoteContacts store');
          const contactStore = db.createObjectStore('purePromoteContacts', { keyPath: 'id' });
          contactStore.createIndex('email', 'email');
          contactStore.createIndex('status', 'status');
          contactStore.createIndex('lastProcessed', 'lastProcessed');
          contactStore.createIndex('contactId', 'contactId');
          contactStore.createIndex('Id', 'Id');
          contactStore.createIndex('Pure_Promote_Last_Contact__c', 'Pure_Promote_Last_Contact__c');
          contactStore.createIndex('Pure_Promote_Last_Location__c', 'Pure_Promote_Last_Location__c');
          contactStore.createIndex('Contact_Notes__c', 'Contact_Notes__c');
          contactStore.createIndex('VIP_Contact__c', 'VIP_Contact__c');
          contactStore.createIndex('billingStateCode', 'billingStateCode');
          contactStore.createIndex('industrySector', 'industrySector');
        }

        if (!db.objectStoreNames.contains('purePromoteReports')) {
          // console.log('Creating purePromoteReports store');
          const reportStore = db.createObjectStore('purePromoteReports', { 
            keyPath: 'id', 
            autoIncrement: true 
          });
          reportStore.createIndex('createdAt', 'createdAt');
          reportStore.createIndex('locationId', 'locationId');
        }

        if (!db.objectStoreNames.contains('emailActivityCache')) {
          // console.log('Creating emailActivityCache store');
          const emailCacheStore = db.createObjectStore('emailActivityCache', { keyPath: 'accountId' });
          emailCacheStore.createIndex('cachedAt', 'cachedAt');
        }

        // console.log('Database upgrade complete');
      },
      blocked(currentVersion, blockedVersion, event) {
        console.warn('Database upgrade blocked. Please close other tabs/windows.', {
          currentVersion,
          blockedVersion,
          event
        });
        
        // Show notification to user
        if (typeof window !== 'undefined') {
          alert('Database upgrade is blocked by another tab. Please close all other tabs of this application and refresh this page.');
          
          // Set a flag to indicate we're waiting for user action
          localStorage.setItem('dbBlockedByOtherTab', 'true');
        }
      },
      blocking(currentVersion, blockedVersion, event) {
        console.warn('This tab is blocking a database upgrade. Reloading...', {
          currentVersion,
          blockedVersion,
          event
        });
        
        // Close the database connection
        if (this && this.db) {
          this.db.close();
        }
        
        // Reload the page to allow other tabs to upgrade
        if (typeof window !== 'undefined') {
          // Clear blocking flag before reloading
          localStorage.removeItem('dbBlockedByOtherTab');
          window.location.reload();
        }
      },
      terminated() {
        console.error('Database connection terminated unexpectedly.');
        
        // Set a flag to attempt recovery on next operation
        this.needsRecovery = true;
      }
    }).catch(error => {
      console.error('Failed to open database:', error);
      
      // Check for specific errors that require different handling
      if (error.name === 'QuotaExceededError') {
        console.warn('Storage quota exceeded. Attempting to clear some space...');
        // Will trigger recovery process
      }
      
      throw error;
    });
  }
  
  // Database recovery method
  async recoverDatabase() {
    console.log(`Attempting database recovery (attempt ${this.recoveryAttempts} of ${this.MAX_RECOVERY_ATTEMPTS})`);
    
    try {
      // Close any existing connections
      if (this.db) {
        this.db.close();
        this.db = null;
      }
      
      // Check if we're blocked by another tab
      if (localStorage.getItem('dbBlockedByOtherTab') === 'true') {
        throw new Error('Database is blocked by another tab. Please close all other tabs of this application.');
      }
      
      // Delete the database to start fresh
      await deleteDatabase();
      
      // Clear any stale flags
      localStorage.removeItem('syncInProgress');
      
      // Reopen the database
      console.log('Reopening database after recovery');
      this.dbPromise = this.openDatabase();
      const db = await this.dbPromise;
      this.db = db;
      
      console.log('Database recovery successful');
      return db;
    } catch (error) {
      console.error('Database recovery failed:', error);
      
      if (this.recoveryAttempts >= this.MAX_RECOVERY_ATTEMPTS) {
        // We've tried too many times, instruct user to manually clear data
        throw new Error('Could not recover database after multiple attempts. Please use the "Force Reset" button in the app header.');
      }
      
      throw error;
    }
  }
  
  // Force a database refresh - can be called when sync is stuck
  async forceRefresh() {
    console.log('Force refreshing database connection');
    
    try {
      // Reset recovery attempts counter
      this.recoveryAttempts = 0;
      
      // Close existing connection
      if (this.db) {
        this.db.close();
        this.db = null;
      }
      
      // Clear database promise
      this.dbPromise = null;
      
      // Clear any stale flags
      localStorage.removeItem('syncInProgress');
      localStorage.removeItem('dbBlockedByOtherTab');
      localStorage.removeItem('lastSyncAttempt');
      
      // Delete the database completely
      await deleteDatabase();
      
      // Reinitialize
      return this.initDB();
    } catch (error) {
      console.error('Force refresh failed:', error);
      throw error;
    }
  }

  async getDb() {
    // Check if we need recovery
    if (this.needsRecovery) {
      this.needsRecovery = false;
      return this.recoverDatabase();
    }
    
    // If DB is not initialized or there was an error, try to initialize again
    if (!this.db || this.initializationError) {
      return this.initDB();
    }
    
    return this.dbPromise || this.initDB();
  }

  // Create a transaction with timeout safeguard
  async createTransaction(storeName, mode = 'readonly') {
    const db = await this.getDb();
    const tx = db.transaction(storeName, mode);
    
    // Create a promise that will reject after the timeout
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Transaction timeout after ${this.transactionTimeout}ms`));
      }, this.transactionTimeout);
    });
    
    // Return a reference to both the transaction and the store
    return {
      tx,
      store: tx.objectStore(storeName),
      // Method to wait for transaction completion with timeout
      async complete() {
        try {
          await Promise.race([tx.done, timeoutPromise]);
        } catch (error) {
          console.error('Transaction failed or timed out:', error);
          throw error;
        }
      }
    };
  }

  // Modified methods to use the new transaction creation pattern
  async getAllClients() {
    try {
      const { store, complete } = await this.createTransaction('clients');
      const results = await store.getAll();
      await complete();
      return results;
    } catch (error) {
      console.error('Error getting all clients:', error);
      // If transaction failed, try to recover and retry once
      if (this.recoveryAttempts < this.MAX_RECOVERY_ATTEMPTS) {
        await this.recoverDatabase();
        const { store } = await this.createTransaction('clients');
        return store.getAll();
      }
      return [];
    }
  }

  async getClientById(id) {
    const db = await this.getDb();
    return db.get('clients', id);
  }

  async searchClients(query) {
    const clients = await this.getAllClients();
    query = query.toLowerCase();
    
    return clients.filter(client => 
      client.clientName.toLowerCase().includes(query) ||
      client.location.toLowerCase().includes(query)
    );
  }

  async getTopClients(limit = 10) {
    const clients = await this.getAllClients();
    return clients
      .sort((a, b) => {
        const aRevenue = a.opportunities.reduce((sum, opp) => 
          sum + (Number(opp.Total_Hire_Fees__c) || 0), 0);
        const bRevenue = b.opportunities.reduce((sum, opp) => 
          sum + (Number(opp.Total_Hire_Fees__c) || 0), 0);
        return bRevenue - aRevenue;
      })
      .slice(0, limit);
  }

  async getRecentClients(days = 90) {
    const clients = await this.getAllClients();
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);

    return clients.filter(client => {
      const lastOpp = client.opportunities[0];
      if (!lastOpp) return false;
      return new Date(lastOpp.CloseDate) >= cutoffDate;
    });
  }

  async getClientMetrics() {
    const clients = await this.getAllClients();
    
    const totalRevenue = clients.reduce((sum, client) => 
      sum + client.opportunities.reduce((oppSum, opp) => 
        oppSum + (Number(opp.Total_Hire_Fees__c) || 0), 0), 0);

    const totalProfit = clients.reduce((sum, client) => 
      sum + client.opportunities.reduce((oppSum, opp) => 
        oppSum + (Number(opp.Net_Profit__c) || 0), 0), 0);

    // Get metrics by type of hire
    const typeOfHireMetrics = clients.reduce((metrics, client) => {
      client.opportunities.forEach(opp => {
        const type = opp.typeOfHire || 'N/A';
        if (!metrics[type]) {
          metrics[type] = {
            count: 0,
            revenue: 0,
            profit: 0
          };
        }
        metrics[type].count++;
        metrics[type].revenue += Number(opp.Total_Hire_Fees__c) || 0;
        metrics[type].profit += Number(opp.Net_Profit__c) || 0;
      });
      return metrics;
    }, {});

    return {
      totalClients: clients.length,
      totalRevenue,
      totalProfit,
      averageRevenuePerClient: totalRevenue / clients.length,
      typeOfHireMetrics
    };
  }

  async getOpportunitiesByTypeOfHire(typeOfHire) {
    const clients = await this.getAllClients();
    return clients.reduce((opportunities, client) => {
      const filteredOpps = client.opportunities.filter(opp => 
        opp.typeOfHire === typeOfHire
      ).map(opp => ({
        ...opp,
        clientName: client.clientName,
        clientId: client.accountId
      }));
      return [...opportunities, ...filteredOpps];
    }, []);
  }

  async getTypeOfHireDistribution(startDate = null, endDate = null) {
    const clients = await this.getAllClients();
    const distribution = {};
    
    clients.forEach(client => {
      client.opportunities.forEach(opp => {
        // Apply date filter if provided
        if (startDate && endDate) {
          const oppDate = new Date(opp.CloseDate);
          if (oppDate < new Date(startDate) || oppDate > new Date(endDate)) {
            return;
          }
        }

        const type = opp.typeOfHire || 'N/A';
        if (!distribution[type]) {
          distribution[type] = {
            count: 0,
            revenue: 0,
            opportunities: []
          };
        }
        distribution[type].count++;
        distribution[type].revenue += Number(opp.Total_Hire_Fees__c) || 0;
        distribution[type].opportunities.push({
          id: opp.Id,
          name: opp.Name,
          clientName: client.clientName,
          closeDate: opp.CloseDate,
          revenue: opp.Total_Hire_Fees__c,
          profit: opp.Net_Profit__c
        });
      });
    });

    return distribution;
  }

  async getLocationsByTypeOfHire() {
    const locations = await this.getAllLocations();
    const distribution = {};
    
    locations.forEach(location => {
      location.potentialLocations.forEach(pl => {
        const type = pl.typeOfHire || 'N/A';
        if (!distribution[type]) {
          distribution[type] = {
            count: 0,
            locations: []
          };
        }
        distribution[type].count++;
        distribution[type].locations.push({
          locationId: location.accountId,
          locationName: location.name,
          potentialLocationId: pl.Id,
          potentialLocationName: pl.Name,
          recceStage: pl.Recce_Stage__c,
          recceStartDateTime: pl.Recce_Start_Date_Time__c
        });
      });
    });

    return distribution;
  }

  async saveClient(client) {
    const db = await this.getDb();
    const tx = db.transaction('clients', 'readwrite');
    await tx.store.put(client);
    await tx.done;
  }

  async saveClients(clients) {
    console.log('Saving clients to IndexedDB:', {
      count: clients?.length || 0,
      sampleClient: clients?.[0]
    });
    
    // Debug: Check if Location_Name__r.BillingAddress data is present
    if (clients && clients.length > 0) {
      const firstClient = clients[0];
      if (firstClient.opportunities && firstClient.opportunities.length > 0) {
        const firstOpp = firstClient.opportunities[0];
        console.log('Sample opportunity being saved:', {
          hasLocation: !!firstOpp.Location_Name__r,
          locationKeys: firstOpp.Location_Name__r ? Object.keys(firstOpp.Location_Name__r) : [],
          billingCountry: firstOpp.Location_Name__r?.BillingCountry,
          billingState: firstOpp.Location_Name__r?.BillingState,
          propertyCategory: firstOpp.Location_Name__r?.Property_Category__c,
          locationData: firstOpp.Location_Name__r
        });
      }
    }
    
    const db = await this.getDb();
    const tx = db.transaction('clients', 'readwrite');
    await Promise.all((clients || []).map(client => tx.store.put(client)));
    await tx.done;
    // console.log('Clients saved successfully');
  }

  async saveLocation(location) {
    const db = await this.getDb();
    const tx = db.transaction('locations', 'readwrite');
    await tx.store.put(location);
    await tx.done;
  }

  async saveLocations(locations) {
    console.log('Saving locations to IndexedDB:', {
      count: locations?.length || 0,
      sampleLocation: locations?.[0]
    });
    const db = await this.getDb();
    const tx = db.transaction('locations', 'readwrite');
    await Promise.all((locations || []).map(location => tx.store.put(location)));
    await tx.done;
    // console.log('Locations saved successfully');
  }

  async getAllLocations() {
    const db = await this.getDb();
    return db.getAll('locations');
  }

  async clearClients() {
    // console.log('Clearing clients store...');
    const db = await this.getDb();
    const tx = db.transaction('clients', 'readwrite');
    await tx.store.clear();
    await tx.done;
    // console.log('Clients store cleared');
  }

  async clearLocations() {
    // console.log('Clearing locations store...');
    const db = await this.getDb();
    const tx = db.transaction('locations', 'readwrite');
    await tx.store.clear();
    await tx.done;
    // console.log('Locations store cleared');
  }

  // Pure Promote specific methods
  async savePurePromoteContacts(contacts) {
    if (!contacts || contacts.length === 0) {
      console.log('No contacts to save');
      return;
    }
    
    try {
      console.log(`Saving ${contacts.length} Pure Promote contacts to IndexedDB`);
      
      // Process in smaller batches to avoid transaction timeouts
      const BATCH_SIZE = 50;
      const batches = [];
      
      for (let i = 0; i < contacts.length; i += BATCH_SIZE) {
        batches.push(contacts.slice(i, i + BATCH_SIZE));
      }
      
      console.log(`Split contacts into ${batches.length} batches of max ${BATCH_SIZE}`);
      
      let successCount = 0;
      
      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i];
        try {
          const { store, complete } = await this.createTransaction('purePromoteContacts', 'readwrite');
          
          // Process each contact in the batch
          await Promise.all(batch.map(async (contact) => {
            try {
              // Add a unique identifier if none exists
              if (!contact.id) {
                contact.id = `${contact.email || contact.Id || Math.random().toString(36).substr(2, 9)}-${Date.now()}`;
              }
              
              // Get existing contact data if it exists
              const existing = await store.get(contact.id);
  
              // Merge existing data with new data, preserving Salesforce fields
              const updatedContact = {
                ...contact,
                status: existing?.status || 'pending',
                lastProcessed: existing?.lastProcessed || null,
                emailsSent: existing?.emailsSent || [],
                notes: existing?.notes || '',
                lastUpdated: new Date().toISOString(),
                // Preserve Salesforce fields
                Id: contact.Id || existing?.Id || null,
                contactId: contact.contactId || contact.Id || existing?.contactId || existing?.Id || null,
                Pure_Promote_Last_Contact__c: contact.Pure_Promote_Last_Contact__c || existing?.Pure_Promote_Last_Contact__c || null,
                Pure_Promote_Last_Location__c: contact.Pure_Promote_Last_Location__c || existing?.Pure_Promote_Last_Location__c || null,
                Contact_Notes__c: contact.Contact_Notes__c !== undefined ? contact.Contact_Notes__c : existing?.Contact_Notes__c,
                VIP_Contact__c: contact.VIP_Contact__c !== undefined ? contact.VIP_Contact__c : existing?.VIP_Contact__c,
                // Filter fields
                ownerId: contact.ownerId || contact.OwnerId || existing?.ownerId || existing?.OwnerId || null,
                ownerName: contact.ownerName || contact.Owner?.Name || existing?.ownerName || null,
                billingStateCode: contact.billingStateCode || contact.BillingStateCode || existing?.billingStateCode || null,
                industrySector: contact.industrySector || contact.Industry_Sectors__c || existing?.industrySector || null,
                // Location fields
                locationName: contact.locationName || existing?.locationName || null,
                locationUrl: contact.locationUrl || existing?.locationUrl || null
              };
  
              await store.put(updatedContact);
              successCount++;
            } catch (error) {
              console.error('Error processing contact:', error);
            }
          }));
  
          await complete();
          console.log(`Successfully saved batch ${i+1}/${batches.length} (${batch.length} contacts)`);
        } catch (batchError) {
          console.error(`Error saving batch ${i+1}:`, batchError);
          // Continue with next batch instead of aborting everything
        }
      }
      
      console.log(`Successfully saved ${successCount}/${contacts.length} contacts`);
      
      if (successCount === 0 && contacts.length > 0) {
        throw new Error('Failed to save any contacts');
      }
      
    } catch (error) {
      console.error('Error saving Pure Promote contacts:', error);
      
      // If no contacts were saved, try to recover and retry once
      if (this.recoveryAttempts < this.MAX_RECOVERY_ATTEMPTS) {
        console.log('Attempting recovery and retry...');
        await this.recoverDatabase();
        this.recoveryAttempts++;
        return this.savePurePromoteContacts(contacts);
      }
      
      throw error;
    }
  }

  async getPurePromoteContacts() {
    try {
      const db = await this.getDb();
      const tx = db.transaction('purePromoteContacts', 'readonly');
      const contacts = await tx.store.getAll();
      
      // Debug log to check data being retrieved
      console.log('Retrieved Pure Promote contacts from IndexedDB:', contacts?.map(contact => ({
        id: contact.id,
        email: contact.email,
        Pure_Promote_Last_Contact__c: contact.Pure_Promote_Last_Contact__c,
        Pure_Promote_Last_Location__c: contact.Pure_Promote_Last_Location__c,
        Contact_Notes__c: contact.Contact_Notes__c,
        VIP_Contact__c: contact.VIP_Contact__c,
        lastInteraction: contact.lastInteraction,
        lastPromoteLocation: contact.lastPromoteLocation
      })));
      
      return contacts;
    } catch (error) {
      console.error('Error getting Pure Promote contacts:', error);
      throw error;
    }
  }

  /**
   * Get only VIP contacts from the database
   * @param {Object} filters - Optional filters to apply
   * @param {string[]} filters.owners - Array of owner IDs to filter by
   * @param {string[]} filters.states - Array of state codes to filter by
   * @param {string[]} filters.industries - Array of industries to filter by
   * @returns {Promise<Array>} Array of contacts with VIP_Contact__c set to true
   */
  async getVIPContacts(filters = {}) {
    try {
      const db = await this.getDb();
      const tx = db.transaction('purePromoteContacts', 'readonly');
      const allContacts = await tx.store.getAll();
      
      // First filter contacts where VIP_Contact__c is true
      let vipContacts = allContacts.filter(contact => contact.VIP_Contact__c === true);
      
      // Apply additional filters if provided
      if (filters.owners && filters.owners.length > 0) {
        vipContacts = vipContacts.filter(contact => 
          contact.ownerId && filters.owners.includes(contact.ownerId)
        );
      }
      
      if (filters.states && filters.states.length > 0) {
        vipContacts = vipContacts.filter(contact => 
          contact.billingStateCode && filters.states.includes(contact.billingStateCode)
        );
      }
      
      if (filters.industries && filters.industries.length > 0) {
        vipContacts = vipContacts.filter(contact => {
          if (!contact.industrySector) return false;
          
          // Handle case where industry is stored as comma-separated values
          const contactIndustries = typeof contact.industrySector === 'string' 
            ? contact.industrySector.split(',').map(i => i.trim())
            : [contact.industrySector];
            
          return filters.industries.some(industry => 
            contactIndustries.includes(industry)
          );
        });
      }
      
      console.log('Retrieved VIP contacts from IndexedDB with filters:', {
        totalContacts: allContacts.length,
        vipContactsTotal: allContacts.filter(c => c.VIP_Contact__c === true).length,
        filteredVipContacts: vipContacts.length,
        appliedFilters: {
          owners: filters.owners?.length || 0,
          states: filters.states?.length || 0,
          industries: filters.industries?.length || 0
        }
      });
      
      return vipContacts;
    } catch (error) {
      console.error('Error getting VIP contacts:', error);
      throw error;
    }
  }

  /**
   * Get unique owner information for VIP contacts
   * @returns {Promise<Array>} Array of unique owners with their IDs and names
   */
  async getUniqueVIPContactOwners() {
    try {
      const vipContacts = await this.getVIPContacts();
      const ownersMap = new Map();
      
      vipContacts.forEach(contact => {
        if (contact.ownerId && contact.ownerName) {
          ownersMap.set(contact.ownerId, {
            id: contact.ownerId,
            name: contact.ownerName
          });
        }
      });
      
      return Array.from(ownersMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
      console.error('Error getting unique VIP contact owners:', error);
      return [];
    }
  }

  /**
   * Get unique state codes for VIP contacts
   * @returns {Promise<Array>} Array of unique state codes
   */
  async getUniqueVIPContactStates() {
    try {
      const vipContacts = await this.getVIPContacts();
      const states = new Set();
      
      vipContacts.forEach(contact => {
        if (contact.billingStateCode) {
          states.add(contact.billingStateCode);
        }
      });
      
      return Array.from(states).sort();
    } catch (error) {
      console.error('Error getting unique VIP contact states:', error);
      return [];
    }
  }

  /**
   * Get unique industries for VIP contacts
   * @returns {Promise<Array>} Array of unique industries
   */
  async getUniqueVIPContactIndustries() {
    try {
      const vipContacts = await this.getVIPContacts();
      const industries = new Set();
      
      vipContacts.forEach(contact => {
        if (contact.industrySector) {
          // Handle comma-separated industry sectors
          const sectors = typeof contact.industrySector === 'string'
            ? contact.industrySector.split(',').map(sector => sector.trim())
            : [contact.industrySector];
            
          sectors.forEach(sector => {
            if (sector) industries.add(sector);
          });
        }
      });
      
      return Array.from(industries).sort();
    } catch (error) {
      console.error('Error getting unique VIP contact industries:', error);
      return [];
    }
  }

  async updatePurePromoteContact(contactId, updates) {
    try {
      const db = await this.getDb();
      const tx = db.transaction('purePromoteContacts', 'readwrite');
      const store = tx.objectStore('purePromoteContacts');

      // First get the existing contact
      const existingContact = await store.get(contactId);
      
      if (!existingContact) {
        throw new Error('Contact not found');
      }

      // Create updated contact with all fields
      const updatedContact = {
        ...existingContact,
        ...updates,
        lastUpdated: new Date().toISOString(),
        // Ensure these fields are always present
        status: updates.status || existingContact.status || 'pending',
        emailsSent: updates.emailsSent || existingContact.emailsSent || [],
        lastProcessed: updates.lastProcessed || existingContact.lastProcessed || null,
        // Preserve Salesforce fields
        Id: updates.Id || existingContact.Id || null,
        contactId: updates.contactId || updates.Id || existingContact.contactId || existingContact.Id || null,
        Pure_Promote_Last_Contact__c: updates.Pure_Promote_Last_Contact__c || existingContact.Pure_Promote_Last_Contact__c || null,
        Pure_Promote_Last_Location__c: updates.Pure_Promote_Last_Location__c || existingContact.Pure_Promote_Last_Location__c || null,
        Contact_Notes__c: updates.Contact_Notes__c !== undefined ? updates.Contact_Notes__c : existingContact.Contact_Notes__c,
        VIP_Contact__c: updates.VIP_Contact__c !== undefined ? updates.VIP_Contact__c : existingContact.VIP_Contact__c,
        // Filter fields
        ownerId: updates.ownerId || updates.OwnerId || existingContact.ownerId || existingContact.OwnerId || null,
        ownerName: updates.ownerName || updates.Owner?.Name || existingContact.ownerName || null,
        billingStateCode: updates.billingStateCode || updates.BillingStateCode || existingContact.billingStateCode || null,
        industrySector: updates.industrySector || updates.Industry_Sectors__c || existingContact.industrySector || null,
        // Location fields
        locationName: updates.locationName || existingContact.locationName || null,
        locationUrl: updates.locationUrl || existingContact.locationUrl || null
      };

      await store.put(updatedContact);
      await tx.complete;
      return updatedContact;
    } catch (error) {
      console.error('Error updating Pure Promote contact:', error);
      throw error;
    }
  }

  async recordEmailSent(contactId, emailDetails) {
    await this.initDB();
    const tx = this.db.transaction('purePromoteContacts', 'readwrite');
    const store = tx.objectStore('purePromoteContacts');

    return new Promise((resolve, reject) => {
      const getRequest = store.get(contactId);
      
      getRequest.onsuccess = () => {
        const contact = getRequest.result;
        if (!contact) {
          reject(new Error('Contact not found'));
          return;
        }

        // Add new email to the history
        const emailRecord = {
          ...emailDetails,
          sentAt: new Date().toISOString()
        };

        const updatedContact = {
          ...contact,
          emailsSent: [...(contact.emailsSent || []), emailRecord],
          lastProcessed: new Date().toISOString(),
          status: 'contacted',
          lastUpdated: new Date().toISOString()
        };

        const putRequest = store.put(updatedContact);
        putRequest.onsuccess = () => resolve(updatedContact);
        putRequest.onerror = () => reject(putRequest.error);
      };
      
      getRequest.onerror = () => reject(getRequest.error);
    });
  }

  async clearPurePromoteContacts() {
    try {
      // console.log('Clearing Pure Promote contacts...');
      const db = await this.getDb();
      const tx = db.transaction('purePromoteContacts', 'readwrite');
      await tx.store.clear();
      await tx.done;
      // console.log('Pure Promote contacts cleared successfully');
    } catch (error) {
      console.error('Error clearing Pure Promote contacts:', error);
      throw error;
    }
  }

  // Pure Promote Report methods
  async savePurePromoteReport(report) {
    try {
      const db = await this.getDb();
      if (!db.objectStoreNames.contains('purePromoteReports')) {
        throw new Error('purePromoteReports store not found. Please reload the application.');
      }

      const reportToSave = {
        ...report,
        id: report.locationId || Date.now().toString(),
        createdAt: new Date().toISOString(),
        lastUpdated: new Date().toISOString()
      };

      const tx = db.transaction('purePromoteReports', 'readwrite');
      await tx.store.put(reportToSave);
      await tx.done;
      return reportToSave;
    } catch (error) {
      console.error('Error saving Pure Promote report:', error);
      throw error;
    }
  }

  async getPurePromoteReport(locationId) {
    try {
      const db = await this.getDb();
      const tx = db.transaction('purePromoteReports', 'readonly');
      const index = tx.store.index('locationId');
      return await index.get(locationId);
    } catch (error) {
      console.error('Error getting Pure Promote report:', error);
      throw error;
    }
  }

  async getAllPurePromoteReports() {
    try {
      const db = await this.getDb();
      return db.getAll('purePromoteReports');
    } catch (error) {
      console.error('Error getting Pure Promote reports:', error);
      throw error;
    }
  }

  async clearPurePromoteReports() {
    try {
      const db = await this.getDb();
      const tx = db.transaction('purePromoteReports', 'readwrite');
      await tx.store.clear();
      await tx.done;
    } catch (error) {
      console.error('Error clearing Pure Promote reports:', error);
      throw error;
    }
  }

  async getUniqueIndustries() {
    const clients = await this.getAllClients();
    const industries = new Set();
    
    clients.forEach(client => {
      if (client.industry && client.industry !== 'N/A') {
        // Handle comma-separated industry sectors
        const sectors = client.industry.split(',').map(sector => sector.trim());
        sectors.forEach(sector => industries.add(sector));
      }
    });

    return Array.from(industries).sort();
  }

  async getUniqueTypeOfHires() {
    const clients = await this.getAllClients();
    const types = new Set();
    
    clients.forEach(client => {
      client.opportunities.forEach(opp => {
        if (opp.typeOfHire && opp.typeOfHire !== 'N/A') {
          types.add(opp.typeOfHire);
        }
      });
    });

    return Array.from(types).sort();
  }

  /**
   * CRITICAL: Ensures all opportunities maintain the complete data structure needed for PurePromote
   * This method must not be modified without careful consideration of its impact on PurePromote
   * 
   * @param {Array} opportunities - Opportunities to ensure have all required fields
   * @returns {Array} - Processed opportunities with all required fields
   */
  ensurePurePromoteCompatibility(opportunities) {
    if (!Array.isArray(opportunities)) {
      console.error('Invalid input: opportunities must be an array');
      return [];
    }

    return opportunities.map(opp => {
      if (!opp) return null;

      // Never modify the original Location_Name__r reference - essential for PurePromote
      // Make sure Primary_Contact__r data is preserved - essential for PurePromote contacts
      
      return {
        ...opp, // Keep ALL original fields
        // Only add fallbacks for missing fields, never override existing ones
        Location_Name__r: opp.Location_Name__r || null,
        Primary_Contact__r: opp.Primary_Contact__r || null
      };
    }).filter(Boolean);
  }

  async filterOpportunities({ industries = [], typeOfHires = [], startDate = null, endDate = null }) {
    const clients = await this.getAllClients();
    const filteredOpportunities = [];

    clients.forEach(client => {
      // Check if client's industry matches any of the selected industries
      const clientIndustries = client.industry.split(',').map(i => i.trim());
      const matchesIndustry = industries.length === 0 || 
        industries.some(industry => clientIndustries.includes(industry));

      if (matchesIndustry) {
        client.opportunities.forEach(opp => {
          // Check if opportunity matches type of hire filter
          const matchesTypeOfHire = typeOfHires.length === 0 || 
            typeOfHires.includes(opp.typeOfHire);

          // Check if opportunity falls within date range
          let matchesDateRange = true;
          if (startDate && endDate) {
            const oppDate = new Date(opp.CloseDate);
            matchesDateRange = oppDate >= new Date(startDate) && 
                             oppDate <= new Date(endDate);
          }

          if (matchesTypeOfHire && matchesDateRange) {
            filteredOpportunities.push({
              ...opp,
              clientName: client.clientName,
              clientId: client.accountId,
              industry: client.industry
            });
          }
        });
      }
    });

    // CRITICAL: Always ensure filtered opportunities maintain PurePromote compatibility
    return this.ensurePurePromoteCompatibility(filteredOpportunities);
  }

  async getFilteredMetrics({ industries = [], typeOfHires = [], startDate = null, endDate = null }) {
    const opportunities = await this.filterOpportunities({ 
      industries, 
      typeOfHires, 
      startDate, 
      endDate 
    });

    const metrics = {
      totalOpportunities: opportunities.length,
      totalRevenue: 0,
      totalProfit: 0,
      byIndustry: {},
      byTypeOfHire: {},
      byMonth: {}
    };

    opportunities.forEach(opp => {
      const revenue = Number(opp.Total_Hire_Fees__c) || 0;
      const profit = Number(opp.Net_Profit__c) || 0;
      
      // Update totals
      metrics.totalRevenue += revenue;
      metrics.totalProfit += profit;

      // Update industry metrics
      const industries = opp.industry.split(',').map(i => i.trim());
      industries.forEach(industry => {
        if (!metrics.byIndustry[industry]) {
          metrics.byIndustry[industry] = { count: 0, revenue: 0, profit: 0 };
        }
        metrics.byIndustry[industry].count++;
        metrics.byIndustry[industry].revenue += revenue;
        metrics.byIndustry[industry].profit += profit;
      });

      // Update type of hire metrics
      const type = opp.typeOfHire || 'N/A';
      if (!metrics.byTypeOfHire[type]) {
        metrics.byTypeOfHire[type] = { count: 0, revenue: 0, profit: 0 };
      }
      metrics.byTypeOfHire[type].count++;
      metrics.byTypeOfHire[type].revenue += revenue;
      metrics.byTypeOfHire[type].profit += profit;

      // Update monthly metrics
      const monthYear = new Date(opp.CloseDate).toISOString().slice(0, 7);
      if (!metrics.byMonth[monthYear]) {
        metrics.byMonth[monthYear] = { count: 0, revenue: 0, profit: 0 };
      }
      metrics.byMonth[monthYear].count++;
      metrics.byMonth[monthYear].revenue += revenue;
      metrics.byMonth[monthYear].profit += profit;
    });

    return metrics;
  }

  /**
   * Sync VIP contacts from Salesforce to IndexedDB
   * @param {Array} vipContacts - VIP contacts from Salesforce
   * @returns {Promise<number>} Number of VIP contacts synced
   */
  async syncVIPContacts(vipContacts) {
    try {
      console.log('Syncing VIP contacts to IndexedDB:', {
        count: vipContacts?.length || 0
      });

      // Use the savePurePromoteContacts method to save VIP contacts
      // This reuses the existing logic and ensures all fields are handled properly
      await this.savePurePromoteContacts(vipContacts);

      console.log('Successfully synced VIP contacts to IndexedDB', {
        count: vipContacts?.length || 0
      });
      
      return vipContacts?.length || 0;
    } catch (error) {
      console.error('Error syncing VIP contacts to IndexedDB:', error);
      throw error;
    }
  }

  /**
   * Cache email activity results for accounts
   * @param {Object} emailActivityMap - Map of accountId to email activity data
   * @returns {Promise<void>}
   */
  async cacheEmailActivity(emailActivityMap) {
    try {
      const db = await this.getDb();
      const tx = db.transaction('emailActivityCache', 'readwrite');
      const store = tx.objectStore('emailActivityCache');
      const cachedAt = Date.now();

      const cacheEntries = Object.entries(emailActivityMap).map(([accountId, emailData]) => ({
        accountId,
        emailData: emailData || null,
        cachedAt
      }));

      await Promise.all(cacheEntries.map(entry => store.put(entry)));
      await tx.done;

      console.log(`Cached email activity for ${cacheEntries.length} accounts`);
    } catch (error) {
      console.error('Error caching email activity:', error);
      // Don't throw - caching failure shouldn't break the app
    }
  }

  /**
   * Get cached email activity for accounts
   * @param {Array<string>} accountIds - Array of account IDs to check
   * @param {number} maxAgeMs - Maximum age of cache in milliseconds (default: 24 hours)
   * @returns {Promise<Object>} Map of accountId to email activity data (only fresh cache entries)
   */
  async getCachedEmailActivity(accountIds, maxAgeMs = 24 * 60 * 60 * 1000) {
    try {
      const db = await this.getDb();
      const tx = db.transaction('emailActivityCache', 'readonly');
      const store = tx.objectStore('emailActivityCache');
      const now = Date.now();
      const results = {};

      for (const accountId of accountIds) {
        try {
          const cached = await store.get(accountId);
          if (cached && cached.cachedAt && (now - cached.cachedAt) < maxAgeMs) {
            // Cache is still valid
            results[accountId] = cached.emailData;
          }
        } catch (error) {
          // Account not in cache or error reading - skip it
          continue;
        }
      }

      await tx.done;
      
      const cachedCount = Object.keys(results).length;
      if (cachedCount > 0) {
        console.log(`Retrieved ${cachedCount} cached email activity entries (out of ${accountIds.length} requested)`);
      }

      return results;
    } catch (error) {
      console.error('Error retrieving cached email activity:', error);
      return {}; // Return empty object on error
    }
  }

  /**
   * Clear expired email activity cache entries
   * @param {number} maxAgeMs - Maximum age of cache in milliseconds (default: 24 hours)
   * @returns {Promise<number>} Number of entries cleared
   */
  async clearExpiredEmailActivityCache(maxAgeMs = 24 * 60 * 60 * 1000) {
    try {
      const db = await this.getDb();
      const tx = db.transaction('emailActivityCache', 'readwrite');
      const store = tx.objectStore('emailActivityCache');
      const index = store.index('cachedAt');
      const now = Date.now();
      const cutoffTime = now - maxAgeMs;
      
      let clearedCount = 0;
      let cursor = await index.openCursor(IDBKeyRange.upperBound(cutoffTime));
      
      while (cursor) {
        await cursor.delete();
        clearedCount++;
        cursor = await cursor.continue();
      }

      await tx.done;
      
      if (clearedCount > 0) {
        console.log(`Cleared ${clearedCount} expired email activity cache entries`);
      }
      
      return clearedCount;
    } catch (error) {
      console.error('Error clearing expired email activity cache:', error);
      return 0;
    }
  }
}

// Helper function to delete the database
async function deleteDatabase() {
  return new Promise((resolve, reject) => {
    const deleteRequest = indexedDB.deleteDatabase(DB_NAME);
    
    // Set a timeout to prevent this operation from hanging
    const timeout = setTimeout(() => {
      console.warn('Database deletion timed out, proceeding anyway');
      resolve();
    }, 10000);
    
    deleteRequest.onsuccess = () => {
      clearTimeout(timeout);
      console.log('Database deleted successfully');
      resolve();
    };
    
    deleteRequest.onerror = (event) => {
      clearTimeout(timeout);
      console.error('Error deleting database:', event);
      reject(new Error('Failed to delete database'));
    };
    
    deleteRequest.onblocked = (event) => {
      clearTimeout(timeout);
      console.warn('Database deletion blocked', event);
      // Try to proceed anyway
      resolve();
    };
  });
}

const indexedDBService = new IndexedDBService();
export default indexedDBService; 